/**
 * Error Reporter
 * 
 * Centralized error reporting and tracking system
 */

import { ErrorReport, MemoryUsage } from '../types';

export class ErrorReporter {
  private static instance: ErrorReporter;
  private errors: ErrorReport[] = [];
  private readonly maxErrors = 100;
  private readonly storageKey = 'syntaxlens_error_reports';

  constructor() {
    this.setupGlobalErrorHandlers();
  }

  static getInstance(): ErrorReporter {
    if (!ErrorReporter.instance) {
      ErrorReporter.instance = new ErrorReporter();
    }
    return ErrorReporter.instance;
  }

  /**
   * Report an error
   */
  reportError(
    error: Error | string,
    component: string,
    severity: ErrorReport['severity'] = 'medium',
    metadata?: Record<string, any>
  ): string {
    const errorId = `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    const report: ErrorReport = {
      id: errorId,
      timestamp: new Date().toISOString(),
      message: error instanceof Error ? error.message : error,
      stack: error instanceof Error ? error.stack : undefined,
      component,
      url: window.location.href,
      userAgent: navigator.userAgent,
      memory: this.getCurrentMemoryUsage(),
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      },
      debugState: this.captureDebugState(),
      severity,
      ...metadata
    };

    this.addError(report);
    return errorId;
  }

  /**
   * Report a performance issue
   */
  reportPerformanceIssue(
    component: string,
    operation: string,
    duration: number,
    threshold: number,
    metadata?: Record<string, any>
  ): string {
    return this.reportError(
      `Performance issue: ${operation} took ${duration}ms (threshold: ${threshold}ms)`,
      component,
      duration > threshold * 2 ? 'high' : 'medium',
      {
        type: 'performance',
        operation,
        duration,
        threshold,
        ...metadata
      }
    );
  }

  /**
   * Report a user action error
   */
  reportUserActionError(
    action: string,
    error: Error | string,
    context?: Record<string, any>
  ): string {
    return this.reportError(
      error,
      'UserAction',
      'low',
      {
        type: 'user_action',
        action,
        ...context
      }
    );
  }

  /**
   * Report an API error
   */
  reportAPIError(
    endpoint: string,
    status: number,
    error: Error | string,
    requestData?: any
  ): string {
    const severity: ErrorReport['severity'] = 
      status >= 500 ? 'high' :
      status === 429 ? 'medium' :
      status >= 400 ? 'low' : 'medium';

    return this.reportError(
      error,
      'API',
      severity,
      {
        type: 'api_error',
        endpoint,
        status,
        requestData: this.sanitizeRequestData(requestData)
      }
    );
  }

  /**
   * Get all error reports
   */
  getErrors(): ErrorReport[] {
    return [...this.errors];
  }

  /**
   * Get error statistics
   */
  getStats(): {
    totalErrors: number;
    byComponent: Record<string, number>;
    bySeverity: Record<string, number>;
    byType: Record<string, number>;
    recentErrors: ErrorReport[];
    errorRate: number; // errors per minute
  } {
    const now = Date.now();
    const oneHourAgo = now - (60 * 60 * 1000);
    const recentErrors = this.errors.filter(e => 
      new Date(e.timestamp).getTime() > oneHourAgo
    );

    const byComponent: Record<string, number> = {};
    const bySeverity: Record<string, number> = {};
    const byType: Record<string, number> = {};

    this.errors.forEach(error => {
      // By component
      byComponent[error.component] = (byComponent[error.component] || 0) + 1;
      
      // By severity
      bySeverity[error.severity] = (bySeverity[error.severity] || 0) + 1;
      
      // By type (from metadata)
      const type = (error as any).type || 'unknown';
      byType[type] = (byType[type] || 0) + 1;
    });

    // Calculate error rate (errors per minute over last hour)
    const errorRate = recentErrors.length / 60;

    return {
      totalErrors: this.errors.length,
      byComponent,
      bySeverity,
      byType,
      recentErrors: recentErrors.slice(-10),
      errorRate
    };
  }

  /**
   * Clear all errors
   */
  clear(): void {
    this.errors = [];
    localStorage.removeItem(this.storageKey);
  }

  /**
   * Export error reports
   */
  exportErrors(): string {
    return JSON.stringify({
      timestamp: new Date().toISOString(),
      stats: this.getStats(),
      errors: this.errors
    }, null, 2);
  }

  /**
   * Add error to collection
   */
  private addError(report: ErrorReport): void {
    this.errors.push(report);

    // Trim if exceeds max
    if (this.errors.length > this.maxErrors) {
      this.errors = this.errors.slice(-this.maxErrors);
    }

    // Console logging
    const style = this.getSeverityStyle(report.severity);
    console.group(`%c[ERROR] ${report.component}`, style);
    console.error('Error:', report.message);
    if (report.stack) {
      console.error('Stack:', report.stack);
    }
    console.error('Report:', report);
    console.groupEnd();

    // Persist to localStorage in dev
    if (import.meta.env.DEV) {
      this.saveToStorage();
    }

    // Send to monitoring service in production (placeholder)
    if (import.meta.env.PROD && report.severity === 'critical') {
      this.sendToMonitoring(report);
    }
  }

  /**
   * Get current memory usage
   */
  private getCurrentMemoryUsage(): MemoryUsage | undefined {
    try {
      if ('memory' in performance) {
        const memory = (performance as any).memory;
        return {
          usedJSHeapSize: memory.usedJSHeapSize,
          totalJSHeapSize: memory.totalJSHeapSize,
          jsHeapSizeLimit: memory.jsHeapSizeLimit
        };
      }
    } catch (e) {
      // Silently fail if memory API not available
    }
    return undefined;
  }

  /**
   * Capture debug state
   */
  private captureDebugState(): any {
    try {
      return {
        directSelections: (window as any).__directSelections,
        debugSteps: (window as any).__debugSteps,
        zenPhase: (window as any).__zenPhase,
        lastError: (window as any).__lastError
      };
    } catch (e) {
      return { captureError: e.message };
    }
  }

  /**
   * Sanitize request data to remove sensitive information
   */
  private sanitizeRequestData(data: any): any {
    if (!data) return data;
    
    const sensitiveFields = ['password', 'token', 'apiKey', 'secret', 'authorization'];
    const sanitized = JSON.parse(JSON.stringify(data));
    
    const sanitizeObject = (obj: any): void => {
      if (typeof obj !== 'object' || obj === null) return;
      
      Object.keys(obj).forEach(key => {
        const lowerKey = key.toLowerCase();
        if (sensitiveFields.some(field => lowerKey.includes(field))) {
          obj[key] = '[REDACTED]';
        } else if (typeof obj[key] === 'object') {
          sanitizeObject(obj[key]);
        }
      });
    };

    sanitizeObject(sanitized);
    return sanitized;
  }

  /**
   * Get severity styling
   */
  private getSeverityStyle(severity: ErrorReport['severity']): string {
    switch (severity) {
      case 'critical': return 'color: #dc2626; font-weight: bold;';
      case 'high': return 'color: #ef4444';
      case 'medium': return 'color: #f59e0b';
      case 'low': return 'color: #6b7280';
      default: return 'color: #374151';
    }
  }

  /**
   * Setup global error handlers
   */
  private setupGlobalErrorHandlers(): void {
    // Unhandled JavaScript errors
    window.addEventListener('error', (event) => {
      this.reportError(
        event.error || event.message,
        'Global',
        'high',
        {
          type: 'unhandled_error',
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno
        }
      );
    });

    // Unhandled Promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.reportError(
        event.reason,
        'Global',
        'high',
        {
          type: 'unhandled_promise_rejection'
        }
      );
    });
  }

  /**
   * Send to monitoring service (placeholder)
   */
  private sendToMonitoring(report: ErrorReport): void {
    // In a real application, this would send to your monitoring service
    // like Sentry, DataDog, New Relic, etc.
    console.info('Error report prepared for monitoring:', report);
  }

  /**
   * Save to localStorage
   */
  private saveToStorage(): void {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.errors));
    } catch (e) {
      console.warn('Failed to save error reports to localStorage:', e);
    }
  }
}

// Export singleton
export const errorReporter = ErrorReporter.getInstance();